import { useCallback, useMemo, useState } from 'react';

enum TimelineItemStatus {
  COMPLETED = 'COMPLETED',
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
}

interface TimelineItem<T extends string | number | symbol> {
  id: T;
  label: string;
  status: TimelineItemStatus;
  isCompleted: boolean;
  disabled?: boolean;
}

interface UseTimelineProps<T extends string | number | symbol> {
  items: TimelineItem<T>[];
}

interface UseTimelineReturn<T extends string | number | symbol> {
  timelineItems: TimelineItem<T>[];
  activeStep: TimelineItem<T> | null;
  getActiveStep: () => TimelineItem<T> | null;
  setActiveStep: (id: T) => void;
  markStepAsCompleted: (id: T) => void;
  resetTimeline: () => void;
}

function useTimeline<T extends string | number | symbol>({
  items,
}: UseTimelineProps<T>): UseTimelineReturn<T> {
  const [timelineItems, setTimelineItems] = useState<TimelineItem<T>[]>(items);

  const activeStep = useMemo(() => {
    return (
      timelineItems.find(
        (item) => item.status === TimelineItemStatus.IN_PROGRESS
      ) || null
    );
  }, [timelineItems]);

  const getActiveStep = useCallback(() => activeStep, [activeStep]);

  const setActiveStep = useCallback((id: T) => {
    setTimelineItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? { ...item, status: TimelineItemStatus.IN_PROGRESS }
          : item.status === TimelineItemStatus.IN_PROGRESS
            ? { ...item, status: TimelineItemStatus.NOT_STARTED }
            : item
      )
    );
  }, []);

  const markStepAsCompleted = useCallback((id: T) => {
    setTimelineItems((prevItems) => {
      const completedStepIndex = prevItems.findIndex((item) => item.id === id);
      const nextStepIndex = completedStepIndex + 1;

      return prevItems.map((item, index) => {
        if (index === completedStepIndex) {
          return {
            ...item,
            status: TimelineItemStatus.COMPLETED,
            isCompleted: true,
          };
        } else if (
          index === nextStepIndex &&
          nextStepIndex < prevItems.length
        ) {
          return { ...item, status: TimelineItemStatus.IN_PROGRESS };
        } else if (item.status === TimelineItemStatus.IN_PROGRESS) {
          return { ...item, status: TimelineItemStatus.NOT_STARTED };
        }
        return item;
      });
    });
  }, []);

  const resetTimeline = useCallback(() => {
    setTimelineItems(
      items.map((item) => ({
        ...item,
        status: TimelineItemStatus.NOT_STARTED,
        isCompleted: false,
      }))
    );
  }, [items]);

  return {
    timelineItems,
    activeStep,
    getActiveStep,
    setActiveStep,
    markStepAsCompleted,
    resetTimeline,
  };
}

export { useTimeline };
